<div class="modal-header">
  <div>
    <div class="small text-muted">Add staff · {{ headerBreadcrumb() }}</div>
    @if (step() === 'lookup') {
      <h2 id="workforce-add-staff-title" class="modal-title h5">Add an Existing Person</h2>
    } @else if (step() === 'person') {
      <h2 id="workforce-add-staff-title" class="modal-title h5">Create a Person</h2>
    } @else {
      <h2 id="workforce-add-staff-title" class="modal-title h5">
        {{ selectedPerson()?.firstName }} {{ selectedPerson()?.lastName }}
        <span class="text-muted fw-normal">· {{ selectedPerson()?.ein }}</span>
      </h2>
    }
  </div>
  <button type="button" class="btn-close" aria-label="Close" (click)="cancel()" [disabled]="isSaving()"></button>
</div>
@if (vacancyStaffPositionId !== undefined && step() !== 'added') {
  <div class="alert alert-info mx-3 mt-3 mb-0" role="status">
    @if (position(); as vacancy) {
      Filling a vacancy:
      <cert-workforce-position-label
        [identifier]="vacancy.positionLocalIdentifier"
        [positionId]="vacancy.id"
        [description]="vacancy.description"
      />. Find the person, then confirm their employment and the assignment to this position.
    } @else {
      @if (temporaryCoverage) {
        This assignment provides temporary coverage while the incumbent is on leave. The position remains Sub-Filling
        FMLA/LOA.
      } @else {
        Filling a vacancy. Find the person, then confirm their employment and the assignment to this position.
      }
    }
  </div>
}
@if (createdPerson(); as person) {
  @if (step() !== 'added') {
    <div class="alert alert-success mx-3 mt-3 mb-0" role="status">
      Person record saved for {{ person.firstName }} {{ person.lastName }} · EIN {{ person.ein }} · EdShID
      {{ person.edShId | guidFormat }}. Add employment details.
    </div>
  }
}
@switch (step()) {
  @case ('person') {
    <cert-workforce-create-person-step
      [leaCodeId]="leaCodeId"
      [searchedEin]="searchedEin() ?? ''"
      [initialIdentity]="lookupFields()"
      (personReady)="acceptIdentity($event)"
      (busyChanged)="identityBusyChanged($event)"
      (cancelled)="cancel()"
      (back)="backToLookup()"
    />
  }
  @case ('lookup') {
    <cert-workforce-add-staff-lookup-step
      [canCreatePerson]="canCreatePerson"
      (createPerson)="beginCreatePerson()"
      [ein]="ein()"
      [fields]="lookupFields()"
      [hasMoreMatches]="hasMoreMatches()"
      (fieldsChanged)="changeLookup($event.field, $event.value, $event.form)"
      [isSearching]="isSearching()"
      [error]="error()"
      [searchedEin]="searchedEin()"
      [matches]="matches()"
      [selectedPerson]="selectedPerson()"
      [errorEmitter]="errorEmitter"
      (einChanged)="changeEin($event.value, $event.form)"
      (personSelected)="selectPerson($event)"
      (cancelled)="cancel()"
      (continueToEmployment)="loadEmployment()"
    />
  }
  @case ('added') {
    <cert-workforce-add-staff-added-step
      [personName]="selectedPerson()?.firstName + ' ' + selectedPerson()?.lastName"
      (returnToRoster)="returnToRoster()"
      (addAnother)="addAnother()"
      (reviewNewStaff)="reviewNewStaff()"
    />
  }
  @default {
    <form #employmentForm="ngForm" autocomplete="off" (ngSubmit)="submitEmployment(employmentForm)">
      <div class="modal-body">
        <div class="alert alert-info">
          A new assignment is created with the Active status.
          <i
            class="bi bi-info-circle"
            aria-hidden="true"
            certTooltip="Unless the staff member is currently on leave. In which case, the assignment inherits the leave status."
          ></i>
        </div>
        @if (isLoadingOptions()) {
          <p role="status">Loading employment choices</p>
        }
        @if (error()) {
          <p class="alert alert-danger" role="alert">{{ error() }}</p>
        }
        <div formLevelValidationErrors [for]="employmentForm" [errorEmitter]="errorEmitter"></div>
        @if (!options() && !isLoadingOptions()) {
          <button type="button" class="btn btn-outline-primary" (click)="loadEmployment()">Retry</button>
        }
        @if (vacancyUnavailable()) {
          <div class="alert alert-warning" role="alert">
            @if (vacancyUnavailableOnSave() && position(); as vacancy) {
              <cert-workforce-position-label
                [identifier]="vacancy.positionLocalIdentifier"
                [positionId]="vacancy.id"
                [description]="vacancy.description"
              />
              was filled, closed or changed before this staff member was saved. No employment or assignment was created.
              @if (createdPerson()) {
                The person record created in this session is kept.
              }
            } @else {
              This position is no longer available to fill. It may have been filled, closed or changed. Nothing has been
              changed.
            }
          </div>
        }
        @if (options() && !vacancyUnavailable()) {
          <fieldset [disabled]="isSaving()" [hidden]="step() === 'confirm'">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="new-work-email" class="form-label">Work Email (Optional)</label>
                <input
                  id="new-work-email"
                  class="form-control"
                  name="workEmail"
                  type="email"
                  #workEmail="ngModel"
                  [ngModel]="draft().workEmail"
                  (ngModelChange)="updateDraft('workEmail', $event)"
                />
                <div validationErrors [for]="workEmail.control" [errorEmitter]="errorEmitter"></div>
              </div>
              <div class="col-md-6">
                <label inputRequired for="date-input-startDate" class="form-label">Employment Start Date</label>
                <cert-date-input name="startDate" [(model)]="startDate" [propertyName]="'startDate'" />
              </div>
              <div class="col-12">
                <label for="new-employment-site" class="form-label">{{
                  requiredSiteId() === null ? 'Site (Optional)' : 'Site'
                }}</label>
                <ng-select
                  inputId="new-employment-site"
                  name="leaSiteCodeId"
                  #site="ngModel"
                  [items]="siteOptions()"
                  bindLabel="description"
                  bindValue="id"
                  [clearable]="false"
                  [readonly]="vacancyStaffPositionId !== undefined || requiredSiteId() !== null"
                  [ngModel]="draft().leaSiteCodeId ?? leaLocationId"
                  (ngModelChange)="updateDraft('leaSiteCodeId', $event === leaLocationId ? undefined : $event)"
                >
                  <ng-template ng-option-tmp let-item="item">
                    {{ item.description }}
                    @if (item.blockedReason) {
                      <small class="d-block text-muted text-wrap">{{ item.blockedReason }}</small>
                    }
                  </ng-template>
                </ng-select>
                <div validationErrors [for]="site.control" [errorEmitter]="errorEmitter"></div>
              </div>
              <div class="col-12">
                <label for="new-employment-position" class="form-label">Position (Optional)</label>
                <ng-select
                  inputId="new-employment-position"
                  name="staffPositionId"
                  #staffPosition="ngModel"
                  [items]="positions()"
                  bindLabel="label"
                  bindValue="id"
                  [clearable]="true"
                  [readonly]="vacancyStaffPositionId !== undefined"
                  placeholder="No assignment"
                  [ngModel]="draft().staffPositionId"
                  (ngModelChange)="updateDraft('staffPositionId', $event)"
                >
                  <ng-template ng-label-tmp let-item="item">
                    <cert-workforce-position-label
                      [identifier]="item.positionLocalIdentifier"
                      [positionId]="item.id"
                      [description]="item.description"
                      [fte]="item.fte"
                      [openDate]="item.openDate"
                    />
                  </ng-template>
                  <ng-template ng-option-tmp let-item="item">
                    <cert-workforce-position-label
                      [identifier]="item.positionLocalIdentifier"
                      [positionId]="item.id"
                      [description]="item.description"
                      [fte]="item.fte"
                      [openDate]="item.openDate"
                    />
                  </ng-template>
                </ng-select>
                <div validationErrors [for]="staffPosition.control" [errorEmitter]="errorEmitter"></div>
              </div>
              @if (position(); as selectedPosition) {
                <div class="col-md-6">
                  <label inputRequired for="date-input-assignmentStartDate" class="form-label"
                    >Assignment Start Date</label
                  >
                  <cert-date-input
                    name="assignmentStartDate"
                    [(model)]="assignmentStartDate"
                    [propertyName]="'assignmentStartDate'"
                  />
                </div>
                <div class="col-md-6">
                  <label [inputRequired]="selectedPosition.isTeaching" for="new-assignment-fte" class="form-label"
                    >Assignment FTE</label
                  >
                  <input
                    id="new-assignment-fte"
                    class="form-control"
                    name="fte"
                    type="number"
                    step="0.2500"
                    #fte="ngModel"
                    [ngModel]="draft().fte"
                    (ngModelChange)="updateDraft('fte', $event)"
                  />
                  <div validationErrors [for]="fte.control" [errorEmitter]="errorEmitter"></div>
                </div>
                @if (selectedPosition.isTeaching) {
                  <div class="col-md-6">
                    <label [inputRequired]="selectedPosition.isTeaching" for="new-periods" class="form-label"
                      >Number of Periods Taught</label
                    >
                    <input
                      id="new-periods"
                      class="form-control"
                      name="periodsTaught"
                      type="number"
                      #periods="ngModel"
                      [ngModel]="draft().periodsTaught"
                      (ngModelChange)="updateDraft('periodsTaught', $event)"
                    />
                    <div validationErrors [for]="periods.control" [errorEmitter]="errorEmitter"></div>
                  </div>
                  <div class="col-md-6">
                    <label [inputRequired]="selectedPosition.isTeaching" for="new-mode" class="form-label"
                      >Mode of Instruction</label
                    >
                    <ng-select
                      inputId="new-mode"
                      name="modeOfInstructionCodeId"
                      #mode="ngModel"
                      [items]="options()!.modes"
                      bindLabel="description"
                      bindValue="id"
                      [clearable]="false"
                      placeholder="Choose a mode"
                      [ngModel]="draft().modeOfInstructionCodeId"
                      (ngModelChange)="updateDraft('modeOfInstructionCodeId', $event)"
                    ></ng-select>
                    <div validationErrors [for]="mode.control" [errorEmitter]="errorEmitter"></div>
                  </div>
                  @if (selectedPosition.isSpecialEd) {
                    <div class="col-md-6">
                      <label inputRequired for="new-teacher-record" class="form-label">Teacher of Record</label>
                      <ng-select
                        inputId="new-teacher-record"
                        name="teacherOfRecord"
                        #teacher="ngModel"
                        [items]="[
                          { id: true, description: 'Yes' },
                          { id: false, description: 'No' },
                        ]"
                        bindLabel="description"
                        bindValue="id"
                        [clearable]="false"
                        placeholder="Choose a response"
                        [ngModel]="draft().teacherOfRecord"
                        (ngModelChange)="updateDraft('teacherOfRecord', $event)"
                      ></ng-select>
                      <div validationErrors [for]="teacher.control" [errorEmitter]="errorEmitter"></div>
                    </div>
                  }
                }
                <div class="col-12">
                  <label for="new-comments" class="form-label">Comments</label>
                  <textarea
                    id="new-comments"
                    class="form-control"
                    name="comments"
                    rows="2"
                    #comments="ngModel"
                    [ngModel]="draft().comments"
                    (ngModelChange)="updateDraft('comments', $event)"
                  ></textarea>
                  <div validationErrors [for]="comments.control" [errorEmitter]="errorEmitter"></div>
                </div>
              }
            </div>
          </fieldset>
          @if (step() === 'confirm') {
            <h3 class="h6">Confirm Employment</h3>
            <dl class="row">
              <dt class="col-sm-4">Location</dt>
              <dd class="col-sm-8">{{ siteName() }}</dd>
              <dt class="col-sm-4">Employment starts</dt>
              <dd class="col-sm-8">{{ startDate ? (startDate | date: 'MM/dd/yyyy') : 'Not entered' }}</dd>
              <dt class="col-sm-4">Work email</dt>
              <dd class="col-sm-8">{{ draft().workEmail || 'Not entered' }}</dd>
              <dt class="col-sm-4">Assignment</dt>
              <dd class="col-sm-8">
                @if (position(); as assignment) {
                  <cert-workforce-position-label
                    [identifier]="assignment.positionLocalIdentifier"
                    [positionId]="assignment.id"
                    [description]="assignment.description"
                  />
                } @else {
                  No assignment
                }
              </dd>
              @if (position()) {
                <dt class="col-sm-4">Assignment starts</dt>
                <dd class="col-sm-8">
                  {{ assignmentStartDate ? (assignmentStartDate | date: 'MM/dd/yyyy') : 'Not entered' }}
                </dd>
                <dt class="col-sm-4">Assignment FTE</dt>
                <dd class="col-sm-8">{{ draft().fte ?? 0 }}</dd>
              }
            </dl>
            @if (vacancyStaffPositionId !== undefined && position(); as vacancy) {
              <div class="alert alert-info mb-0">
                <cert-workforce-position-label
                  [identifier]="vacancy.positionLocalIdentifier"
                  [positionId]="vacancy.id"
                  [description]="vacancy.description"
                />
                will be filled by this new staff member. Their record will need roster verification.
              </div>
            }
          }
        }
      </div>
      <div class="modal-footer">
        @if (vacancyUnavailable()) {
          <button type="button" class="btn btn-primary" (click)="cancel()">Return to Positions</button>
        } @else {
          <button type="button" class="btn btn-outline-secondary" [disabled]="isSaving()" (click)="cancel()">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-outline-primary"
            [disabled]="isSaving() || isLoadingOptions()"
            (click)="goBack()"
          >
            Back
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            [certAutofocus]="step() === 'confirm'"
            [disabled]="isSaving() || !options()"
          >
            {{ employmentSubmitLabel() }}
          </button>
        }
      </div>
    </form>
  }
}
